<template>
  <div class="app-container">
    <div class="page-header">
      <div class="back-link" @click="goBack">
        <i class="el-icon-arrow-left"></i> 返回
      </div>
      <div class="page-title">作业列表</div>
    </div>

    <!-- 作业列表内容 -->
    <div class="homework-list-content">
      <el-card class="box-card">
        <div slot="header" class="clearfix">
          <span>作业列表</span>
          <el-button
            style="float: right; padding: 3px 0"
            type="text"
            icon="el-icon-refresh"
            @click="fetchHomeworkList"
          >刷新</el-button>
        </div>

        <el-table
          v-loading="loading"
          :data="homeworkList"
          border
          fit
          highlight-current-row
          style="width: 100%"
        >
          <el-table-column
            align="center"
            label="序号"
            width="80"
            type="index"
          >
          </el-table-column>

          <el-table-column
            align="center"
            prop="title"
            label="作业名称"
            min-width="180"
          >
          </el-table-column>

          <el-table-column
            align="center"
            prop="publishTime"
            label="发布时间"
            min-width="150"
          >
            <template slot-scope="scope">
              {{ formatDate(scope.row.publishTime) }}
            </template>
          </el-table-column>

          <el-table-column
            align="center"
            prop="deadline"
            label="截止时间"
            min-width="150"
          >
            <template slot-scope="scope">
              {{ formatDate(scope.row.deadline) }}
            </template>
          </el-table-column>

          <el-table-column
            align="center"
            prop="status"
            label="状态"
            width="100"
          >
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row.status)">
                {{ scope.row.status || '进行中' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column
            align="center"
            label="操作"
            width="150"
          >
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                @click="viewHomeworkDetail(scope.row.id)"
              >查看详情</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            background
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 30, 50]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
          >
          </el-pagination>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { getHomeworkList } from '@/api/homework'
import { parseTime } from '@/utils'

export default {
  name: 'HomeworkList',
  data() {
    return {
      loading: false,
      homeworkList: [],
      currentPage: 1,
      pageSize: 10,
      total: 0
    }
  },
  created() {
    // 检查用户角色
    const userRole = localStorage.getItem('roles')
    if (userRole !== 'teacher' && userRole !== 'mteacher') {
      this.$message.error('您没有权限访问此页面')
      this.$router.push('/404')
      return
    }

    this.fetchHomeworkList()
  },
  methods: {
    // 获取作业列表
    async fetchHomeworkList() {
      this.loading = true
      try {
        const response = await getHomeworkList()
        if (response.code === 1 && response.data) {
          // 将API返回的数据映射到表格需要的格式
          this.homeworkList = response.data.map(item => ({
            id: item.id,
            title: item.name, // 将name映射为title
            className: `${item.subject}班`, // 使用subject作为班级名称
            publishTime: item.createTime, // 将createTime映射为publishTime
            deadline: item.createTime, // 暂时使用createTime作为deadline
            status: this.getStatusFromCount(item.count) // 根据count生成状态
          }))
          this.total = response.data.length
          console.log('处理后的作业列表:', this.homeworkList)
        } else {
          // 如果API返回错误或没有数据，使用模拟数据
          this.homeworkList = this.getMockData()
          this.total = this.homeworkList.length
          console.warn('使用模拟数据：', response.message || '获取作业列表失败')
        }
      } catch (error) {
        console.error('获取作业列表失败:', error)
        this.$message.error('获取作业列表失败，请稍后重试')
        // 使用模拟数据
        this.homeworkList = this.getMockData()
        this.total = this.homeworkList.length
      } finally {
        this.loading = false
      }
    },

    // 根据count生成状态
    getStatusFromCount(count) {
      if (count === undefined || count === null) return '进行中'
      if (count >= 20) return '已完成'
      if (count >= 10) return '已截止'
      return '进行中'
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return '暂无数据'
      return parseTime(new Date(date), '{y}-{m}-{d} {h}:{i}')
    },

    // 获取状态标签类型
    getStatusType(status) {
      const statusMap = {
        '进行中': 'primary',
        '已截止': 'warning',
        '已完成': 'success'
      }
      return statusMap[status] || 'info'
    },

    // 查看作业详情
    viewHomeworkDetail(id) {
      this.$router.push(`/score-analysis/homework/detail?id=${id}`)
    },

    // 返回上一页
    goBack() {
      this.$router.push('/score-analysis/homework-analysis')
    },

    // 处理每页显示数量变化
    handleSizeChange(val) {
      this.pageSize = val
      this.fetchHomeworkList()
    },

    // 处理页码变化
    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchHomeworkList()
    },

    // 获取模拟数据（当API调用失败时使用）
    getMockData() {
      return [
        {
          id: 1,
          title: '第一章习题作业',
          className: '高一(1)班',
          publishTime: '2023-09-01 10:00:00',
          deadline: '2023-09-10 23:59:59',
          status: '已完成'
        },
        {
          id: 2,
          title: '第二章习题作业',
          className: '高一(2)班',
          publishTime: '2023-09-15 14:30:00',
          deadline: '2023-09-25 23:59:59',
          status: '已截止'
        },
        {
          id: 3,
          title: '第三章习题作业',
          className: '高一(1)班',
          publishTime: '2023-10-01 09:00:00',
          deadline: '2023-10-10 23:59:59',
          status: '进行中'
        },
        {
          id: 4,
          title: '期中复习作业',
          className: '高一(3)班',
          publishTime: '2023-10-15 16:00:00',
          deadline: '2023-10-20 23:59:59',
          status: '进行中'
        },
        {
          id: 5,
          title: '第四章习题作业',
          className: '高一(2)班',
          publishTime: '2023-10-25 11:30:00',
          deadline: '2023-11-05 23:59:59',
          status: '进行中'
        }
      ]
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.back-link {
  cursor: pointer;
  color: #409EFF;
  font-size: 14px;
  margin-right: 15px;
  display: flex;
  align-items: center;
}

.back-link:hover {
  opacity: 0.8;
}

.page-title {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
}

.homework-list-content {
  background-color: #fff;
  border-radius: 4px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
